import type { PlacedOrder, RazorpaySuccess } from "./types";

/** Razorpay Standard Checkout, loaded on demand from Razorpay's CDN. */

const SCRIPT_URL = "https://checkout.razorpay.com/v1/checkout.js";

interface RazorpayInstance {
  open: () => void;
  on: (event: "payment.failed", handler: (response: { error?: { description?: string } }) => void) => void;
}
type RazorpayConstructor = new (options: Record<string, unknown>) => RazorpayInstance;

let loading: Promise<RazorpayConstructor> | null = null;

function loadRazorpay(): Promise<RazorpayConstructor> {
  const existing = (window as unknown as { Razorpay?: RazorpayConstructor }).Razorpay;
  if (existing) return Promise.resolve(existing);
  loading ??= new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = SCRIPT_URL;
    script.async = true;
    script.onload = () => resolve((window as unknown as { Razorpay: RazorpayConstructor }).Razorpay);
    script.onerror = () => {
      loading = null;
      reject(new Error("Could not load Razorpay Checkout. Check your connection and try again."));
    };
    document.body.appendChild(script);
  });
  return loading;
}

/**
 * Opens Checkout for a placed order. Resolves with Razorpay's signed result; rejects
 * if the buyer closes Checkout (with the last failure, if an attempt failed).
 */
export async function openRazorpayCheckout({ order, razorpay_key_id }: PlacedOrder): Promise<RazorpaySuccess> {
  const Razorpay = await loadRazorpay();
  return new Promise((resolve, reject) => {
    // A failed attempt keeps Checkout open so the buyer can retry; report it only if
    // they then close the window.
    let lastError: string | null = null;
    const checkout = new Razorpay({
      key: razorpay_key_id,
      order_id: order.ref_order_id,
      amount: order.amount_paise,
      currency: order.currency,
      name: "TechLadder",
      description: `Order ${order.reference_number}`,
      handler: (result: RazorpaySuccess) => resolve(result),
      modal: { ondismiss: () => reject(new Error(lastError || "Payment cancelled.")) },
    });
    checkout.on("payment.failed", (response) => {
      lastError = response.error?.description || "Payment failed.";
    });
    checkout.open();
  });
}
